import React, { useMemo } from 'react';
import { Container, Form, Table } from 'react-bootstrap';
import { getComparator, stableSort } from '../../lib/utils';
import PaginationComponent from './PaginationComponent';
import { Link } from 'react-router-dom';
import { Close, North, South } from '@mui/icons-material';
import TableLoader from './TableLoader';
import PaginationInfo from './PaginationInfo';
import { IconButton, Typography } from '@mui/material';
import TableColumnManager from './TableColumnManager';

const defaultIsRowDisabled = () => false;

const TableComponent = ({
  data,
  headerTabs,
  page,
  perPage,
  showCheckbox,
  action,
  defaultSort,
  order,
  setOrder,
  selected,
  setSelected,
  showPages,
  striped,
  isLoading,
  setSelectedRows = () => {},
  totalCount,
  handlePageChange,
  tablekey,
  isRowDisabled = defaultIsRowDisabled,
  checkboxDisabledTitle,
  showCount,
  setPerPage,
  showRowCount = false,
  showColumnManager = false,
  selectedColumns,
  setSelectedColumns,
  mandatoryKeys = null,
}) => {
  const [orderBy, setOrderBy] = React.useState(defaultSort || '');
  const visibleHeaders = useMemo(() => {
    if (!showColumnManager || !selectedColumns?.length) return headerTabs;
    return headerTabs.filter((header) => selectedColumns.includes(header.key));
  }, [headerTabs, selectedColumns, showColumnManager]);
  const isAllSelected = useMemo(() => {
    const selectableRows = data?.filter((row) => !isRowDisabled(row) && !row.isRowDisabled) || [];
    const selectableIds = selectableRows.map((r) => r.id);
    
    if (selectableIds.length === 0) return false;
    
    return selectableIds.every((id) => selected?.includes(id));
  }, [data, selected, isRowDisabled]);

  const isSelected = (name) => selected && selected.indexOf(name) !== -1;
  const handleClick = (event, name, row) => {
    if (isRowDisabled(row)) return;

    const selectedIndex = selected?.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
    setSelectedRows((prevRows) => {
      const combined = [...prevRows, ...data];
      const unique = Array.from(new Map(combined.map((r) => [r.id, r])).values());
      return unique.filter((r) => newSelected.includes(r.id));
    });
  };

  const handleSelectAll = (checked) => {
    const selectableRows = data?.filter((row) => !isRowDisabled(row) && !row.isRowDisabled) || [];
    const selectableIds = selectableRows.map((r) => r.id);

    if (checked) {
      const mergedSelections = Array.from(new Set([...selected, ...selectableIds]));
      setSelected(mergedSelections);
      setSelectedRows((prevRows) => {
        const combined = [...prevRows, ...selectableRows];
        const unique = Array.from(new Map(combined.map((r) => [r.id, r])).values());
        return unique.filter((r) => mergedSelections.includes(r.id));
      });
    } else {
      setSelected(selected.filter((id) => !selectableIds.includes(id)));
      setSelectedRows((prev) => prev.filter((row) => !selectableIds.includes(row.id)));
    }
  };

  const handleSort = (key) => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setOrderBy(key);
  };

  return (
    <Container fluid className="mt-4 px-0">
      <div
        className={`d-flex ${
          selected?.length > 0
            ? 'justify-content-between'
            : 'justify-content-end'
        } mb-2 gap-2`}>
        {selected?.length > 0 && (
          <div className="d-flex justify-content-start align-items-center gap-3">
            <Typography sx={{ color: '#1b3f87', marginBottom: 0 }}>
              {selected?.length} records selected
            </Typography>
            <IconButton
              size="small"
              title="Clear Selection"
              onClick={() => {
                setSelected([]);
                setSelectedRows([]);
              }}
              sx={{
                border: '1px solid',
                borderColor: 'error.main',
                color: 'error.main',
                padding: '1px',
              }}>
              <Close fontSize="12px" />
            </IconButton>
          </div>
        )}
        <div className="d-flex gap-2 align-items-center">
          {showCount && (
            <PaginationInfo
              currentPage={page}
              perPage={perPage}
              totalCount={totalCount}
              showText=""
              showCenter={false}
              setPerPage={setPerPage}
              showRowCount={showRowCount}
            />
          )}
          {showColumnManager && (
            <TableColumnManager
              allHeaders={headerTabs}
              selectedKeys={selectedColumns}
              onSelectionChange={setSelectedColumns}
              mandatoryKeys={mandatoryKeys || ['key', 'summary']}
            />
          )}
        </div>
      </div>

      <div style={{ overflowX: 'auto', width: '100%' }}>
        <Table
          striped={striped}
          responsive
          className="border"
          style={{ minWidth: '800px' }}>
          <thead>
            <tr>
              {showCheckbox && !isLoading && (
                <th className="text-center" style={{ minWidth: '50px' }}>
                  <Form.Check
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
              )}
              {visibleHeaders.map((tab) => (
                <th
                  title={tab.hoverText || tab.label}
                  className={tab?.sortable ? 'cursor-pointer' : 'cursor-arrow'}
                  key={tab.key}
                  style={{
                    minWidth: tab.minWidth || '100px',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={
                    tab?.sortable ? () => handleSort(tab.key) : () => {}
                  }>
                  {tab.label}{' '}
                  {tab?.sortable && tab.key === orderBy ? (
                    order === 'asc' ? (
                      <North fontSize="small" />
                    ) : (
                      <South fontSize="small" />
                    )
                  ) : null}
                </th>
              ))}
              {action && <th style={{ minWidth: '100px' }}></th>}
            </tr>
          </thead>
          {isLoading ? (
            <TableLoader
              colSpan={
                visibleHeaders.length +
                (action ? 1 : 0) +
                (showCheckbox ? 1 : 0)
              }
            />
          ) : (
            <tbody>
              {data?.length === 0 && (
                <tr>
                  <td
                    colSpan={
                      visibleHeaders.length +
                      (action ? 1 : 0) +
                      (showCheckbox ? 1 : 0)
                    }
                    className="text-center text-muted">
                    No {tablekey || 'data'} available
                  </td>
                </tr>
              )}
              {stableSort(data, getComparator(order, orderBy)).map(
                (row, rowIndex) => {
                  const isItemSelected = isSelected(row.id);
                  const disabled = isRowDisabled(row);
                  return (
                    <tr
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}
                      title={
                        row.isRowDisabled
                          ? checkboxDisabledTitle
                          : disabled
                          ? 'Already added'
                          : ''
                      }
                      className={disabled ? 'table-secondary' : ''}>
                      {showCheckbox && (
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={!!isItemSelected || disabled}
                            onChange={(event) => handleClick(event, row.id, row)}
                            disabled={disabled || row.isRowDisabled}
                            className={disabled ? 'checkbox-disabled' : ''}
                          />
                        </td>
                      )}
                      {visibleHeaders.map(
                        (
                          {
                            key,
                            onClick,
                            paramToPass,
                            linkToId,
                            formatContent,
                          },
                          index
                        ) => {
                          return (
                            <td key={'col' + index + rowIndex} align="left">
                              {formatContent ? (
                                formatContent(row, rowIndex, page, perPage)
                              ) : linkToId ? (
                                <Link to={`/${linkToId}/${row.id}`}>
                                  {row[key]}
                                </Link>
                              ) : (
                                row[key] || '-'
                              )}
                            </td>
                          );
                        }
                      )}
                      {action && (
                        <td className="row-action" align="left">
                          {action(row)}
                        </td>
                      )}
                    </tr>
                  );
                }
              )}
            </tbody>
          )}
        </Table>
      </div>

      {showPages && (
        <PaginationComponent
          totalCount={totalCount}
          currentPage={page}
          handlePageChange={handlePageChange}
          perPage={perPage}
        />
      )}
    </Container>
  );
};

export default TableComponent;
